import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import ubicacionService, { Ubicacion, etiquetaUbicacionSeleccion } from '../services/ubicacionService';
import { SearchableObjectSelect } from './SearchableSelect';
import { useUbicacionesUsuarioContext } from '../contexts/UbicacionesUsuarioContext';

interface Props {
  value: string;
  onChange: (value: string, nroUbicacion?: number) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  label?: string;
  fullWidth?: boolean;
  autoSelectPreferida?: boolean;
}

const parseNroDesdeValor = (s: string): number | null => {
  const m = String(s ?? '').trim().match(/\((\d+)\)\s*$/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const UbicacionSelector: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  size = 'small',
  label = 'Ubicación / Depósito de Entrega',
  fullWidth = true,
  autoSelectPreferida = true,
}) => {
  const { preferidaUbicacion, sinAsignaciones, loading: loadingCtx } = useUbicacionesUsuarioContext();
  const [ubicacionesLista, setUbicacionesLista] = useState<Ubicacion[]>([]);
  const [loadingUbicaciones, setLoadingUbicaciones] = useState(true);

  const effectiveDisabled = disabled || sinAsignaciones;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingUbicaciones(true);
      try {
        const list = await ubicacionService.getUbicacionesParaUsuario();
        if (!cancelled) setUbicacionesLista(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setUbicacionesLista([]);
      } finally {
        if (!cancelled) setLoadingUbicaciones(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!autoSelectPreferida || effectiveDisabled) return;
    if ((value ?? '').trim()) return;
    if (!preferidaUbicacion) return;
    onChange(
      etiquetaUbicacionSeleccion(preferidaUbicacion),
      preferidaUbicacion.nroUbicacion
    );
  }, [autoSelectPreferida, effectiveDisabled, value, preferidaUbicacion, onChange]);

  const ubicacionesOpciones = useMemo((): Ubicacion[] => {
    const sorted = [...ubicacionesLista].sort((a, b) =>
      etiquetaUbicacionSeleccion(a).localeCompare(etiquetaUbicacionSeleccion(b), 'es'),
    );
    const raw = (value ?? '').trim();
    if (!raw) return sorted;
    const tiene = sorted.some((u) => etiquetaUbicacionSeleccion(u) === raw);
    if (tiene) return sorted;
    const nro = parseNroDesdeValor(raw);
    if (nro != null) {
      const nombreSinNro = raw.replace(/\s*\(\d+\)\s*$/, '').trim();
      return [
        {
          nroUbicacion: nro,
          nombreUbicacion: nombreSinNro || `Ubicación ${nro}`,
          nroSuc: 0,
        },
        ...sorted,
      ];
    }
    return sorted;
  }, [ubicacionesLista, value]);

  if (loadingUbicaciones || loadingCtx) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  return (
    <SearchableObjectSelect
      label={label}
      size={size}
      fullWidth={fullWidth}
      disabled={effectiveDisabled}
      allowEmpty
      emptyOptionLabel="Seleccionar"
      placeholder="Buscar ubicación..."
      options={ubicacionesOpciones}
      value={(value ?? '').trim() || null}
      onChange={(_, ubicacion) => {
        if (!ubicacion) {
          onChange('', undefined);
          return;
        }
        const etiqueta = etiquetaUbicacionSeleccion(ubicacion);
        onChange(etiqueta, ubicacion.nroUbicacion);
      }}
      getOptionValue={(u) => etiquetaUbicacionSeleccion(u)}
      getOptionLabel={(u) => etiquetaUbicacionSeleccion(u)}
      isOptionEqualToValue={(option, current) =>
        etiquetaUbicacionSeleccion(option) === current
      }
    />
  );
};

export default UbicacionSelector;
